/**
 * Manages a single background polling loop with optional jitter.
 * Uses chained timeouts (not setInterval) so each cycle can vary in length.
 * Ensures at most one timer is active at any time.
 */
export class DeploymentPollScheduler {
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private readonly onPoll: () => void;
  private readonly intervalMs: number;
  private readonly jitterMs: number;

  constructor(onPoll: () => void, intervalMs: number, jitterMs = 0) {
    this.onPoll = onPoll;
    this.intervalMs = intervalMs;
    this.jitterMs = jitterMs;
  }

  start(): void {
    this.stop();
    this.running = true;
    this.scheduleNext();
  }

  stop(): void {
    this.running = false;

    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  isRunning(): boolean {
    return this.running;
  }

  private scheduleNext(): void {
    if (!this.running) {
      return;
    }

    this.timerId = setTimeout(() => {
      if (!this.running) {
        return;
      }

      this.onPoll();
      this.scheduleNext();
    }, this.getNextInterval());
  }

  private getNextInterval(): number {
    if (this.jitterMs <= 0) {
      return this.intervalMs;
    }

    const jitter = Math.floor(Math.random() * (this.jitterMs + 1));
    return this.intervalMs + jitter;
  }
}
